window.ThemeCart = window.ThemeCart || {};

class ThemeCartApi {
  static cartCache = null;
  static cartCacheDirty = true;
  static cartFetchPromise = null;

  static localeRootUrl() {
    return window.routes?.localeRootUrl || "";
  }

  static url(path) {
    return `${ThemeCartApi.localeRootUrl()}${path}`;
  }

  static jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    };
  }

  static setCartCache(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    ThemeCartApi.cartCache = cart;
    ThemeCartApi.cartCacheDirty = false;
  }

  static markCartCacheDirty() {
    ThemeCartApi.cartCacheDirty = true;
  }

  static async waitForPromise(promise, signal) {
    if (!signal) return promise;
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");

    return new Promise((resolve, reject) => {
      const onAbort = () => reject(new DOMException("Aborted", "AbortError"));
      signal.addEventListener("abort", onAbort, { once: true });
      promise.then(
        (value) => {
          signal.removeEventListener("abort", onAbort);
          resolve(value);
        },
        (error) => {
          signal.removeEventListener("abort", onAbort);
          reject(error);
        }
      );
    });
  }

  static async cacheFullCartResponse(response) {
    if (!response?.ok) return;
    try {
      const cart = await response.clone().json();
      ThemeCartApi.setCartCache(cart);
    } catch (e) {
      ThemeCartApi.markCartCacheDirty();
    }
  }

  static async fetchFreshCart(signal) {
    const res = await fetch(ThemeCartApi.url("/cart.js"), {
      credentials: "same-origin",
      signal,
    });
    if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
    const cart = await res.json();
    ThemeCartApi.setCartCache(cart);
    return cart;
  }

  static async fetchCart(signal) {
    if (ThemeCartApi.cartCache && !ThemeCartApi.cartCacheDirty) {
      return ThemeCartApi.cartCache;
    }

    if (!ThemeCartApi.cartFetchPromise) {
      ThemeCartApi.cartFetchPromise = ThemeCartApi.fetchFreshCart().finally(() => {
        ThemeCartApi.cartFetchPromise = null;
      });
    }

    return ThemeCartApi.waitForPromise(ThemeCartApi.cartFetchPromise, signal);
  }

  static async change(body, signal) {
    const response = await fetch(ThemeCartApi.url("/cart/change.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: ThemeCartApi.jsonHeaders(),
      body: JSON.stringify(body),
      signal,
    });
    await ThemeCartApi.cacheFullCartResponse(response);
    return response;
  }

  static async add(body, signal) {
    const response = await fetch(ThemeCartApi.url("/cart/add.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: ThemeCartApi.jsonHeaders(),
      body: JSON.stringify(body),
      signal,
    });
    if (response.ok) ThemeCartApi.markCartCacheDirty();
    return response;
  }

  static async update(body, signal) {
    const response = await fetch(ThemeCartApi.url("/cart/update.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: ThemeCartApi.jsonHeaders(),
      body: JSON.stringify(body),
      signal,
    });
    await ThemeCartApi.cacheFullCartResponse(response);
    return response;
  }
}

class ThemeCartLineRules {
  static propertiesEntries(item) {
    const props = item?.properties;
    if (props == null || props === false) return [];
    if (Array.isArray(props)) {
      return props.map((p) => {
        const name = (p?.name ?? p?.first ?? "").toString();
        const value = p?.value ?? p?.last;
        return [name, value];
      });
    }
    if (typeof props === "object") {
      return Object.keys(props).map((k) => [k, props[k]]);
    }
    return [];
  }

  static propertyValue(item, wantedLower) {
    const w = String(wantedLower || "").toLowerCase();
    for (const [name, value] of ThemeCartLineRules.propertiesEntries(item)) {
      const n = String(name || "").trim().toLowerCase();
      if (n !== w) continue;
      if (value == null) return "";
      return String(value).trim();
    }
    return "";
  }

  static propertyRaw(item, propertyKey) {
    const props = item?.properties;
    if (props == null || props === false) return undefined;
    if (typeof props === "object" && !Array.isArray(props)) {
      if (propertyKey != null && Object.prototype.hasOwnProperty.call(props, propertyKey)) {
        return props[propertyKey];
      }
      const want = String(propertyKey).toLowerCase();
      const keys = Object.keys(props);
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (String(k).toLowerCase() === want) return props[k];
      }
      return undefined;
    }
    if (Array.isArray(props)) {
      const want = String(propertyKey).toLowerCase();
      for (let i = 0; i < props.length; i++) {
        const p = props[i];
        if (!p || typeof p !== "object") continue;
        const name = p.name != null ? p.name : p.first;
        if (String(name).toLowerCase() === want) {
          return p.value !== undefined && p.value !== null ? p.value : p.last;
        }
      }
    }
    return undefined;
  }

  static readBundleId(item) {
    return ThemeCartLineRules.propertyValue(item, "_bundle_id");
  }

  static readBundleRole(item) {
    return (ThemeCartLineRules.propertyValue(item, "_bundle_role") || "").toLowerCase();
  }

  static isBundleFrame(item) {
    return ThemeCartLineRules.readBundleRole(item) === "frame";
  }

  static itemDomIdFromLineKey(key) {
    return `cart-item__${String(key || "").replace(/[:/.]/g, "-")}`;
  }

  static lineTotalCents(item) {
    if (item == null) return 0;
    if (typeof item.final_line_price === "number") return item.final_line_price;
    if (typeof item.line_price === "number") return item.line_price;
    const q = typeof item.quantity === "number" && item.quantity > 0 ? item.quantity : 1;
    const unit = typeof item.price === "number" ? item.price : 0;
    return unit * q;
  }

  static hasThemeFreeGiftMarker(item) {
    for (const [name, value] of ThemeCartLineRules.propertiesEntries(item)) {
      const key = String(name || "").trim().toLowerCase();
      if (key !== "_free_gift" && key !== "free_gift") continue;
      if (value === true) return true;
      const v = String(value ?? "").trim().toLowerCase();
      if (v === "" || v === "true" || v === "yes" || v === "1") return true;
    }
    return false;
  }
}

class ThemeBundleCartRules {
  static cartHasOrphanBundleLines(cartData) {
    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    const byBundle = new Map();
    for (const item of items) {
      const bid = ThemeCartLineRules.readBundleId(item);
      if (!bid) continue;
      if (!byBundle.has(bid)) byBundle.set(bid, []);
      byBundle.get(bid).push(item);
    }
    for (const group of byBundle.values()) {
      const hasFrame = group.some((it) => ThemeCartLineRules.readBundleRole(it) === "frame");
      if (!hasFrame) return true;
    }
    return false;
  }

  static removalUpdatesForQtyZero(cartData, lineId) {
    const fallback = { [String(lineId)]: 0 };
    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    const target = items.find((i) => String(i.key) === String(lineId));
    if (!target) return fallback;
    const bundleId = ThemeCartLineRules.readBundleId(target);
    if (!bundleId) return fallback;
    const updates = {};
    for (const item of items) {
      if (ThemeCartLineRules.readBundleId(item) === bundleId && item?.key) {
        updates[String(item.key)] = 0;
      }
    }
    return Object.keys(updates).length ? updates : fallback;
  }

  static async fetchRemovalUpdatesForQtyZero(lineId, signal) {
    try {
      const cartJson = await ThemeCartApi.fetchCart(signal);
      return ThemeBundleCartRules.removalUpdatesForQtyZero(cartJson, lineId);
    } catch (e) {
      if (e?.name === "AbortError") throw e;
      return { [String(lineId)]: 0 };
    }
  }

  static orphanedBundleRemovalUpdates(cartData) {
    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    const byBundle = new Map();
    for (const item of items) {
      const bid = ThemeCartLineRules.readBundleId(item);
      if (!bid) continue;
      if (!byBundle.has(bid)) byBundle.set(bid, []);
      byBundle.get(bid).push(item);
    }

    const keys = [];
    for (const group of byBundle.values()) {
      const hasFrame = group.some((it) => ThemeCartLineRules.readBundleRole(it) === "frame");
      if (!hasFrame) {
        group.forEach((it) => {
          if (it?.key) keys.push(String(it.key));
        });
      }
    }

    const unique = [...new Set(keys)];
    const updates = {};
    unique.forEach((k) => {
      updates[k] = 0;
    });
    return updates;
  }

  static async removeOrphanedBundleLinesFromCart(cartData, signal) {
    const updates = ThemeBundleCartRules.orphanedBundleRemovalUpdates(cartData);
    if (!Object.keys(updates).length) return null;
    const res = await ThemeCartApi.update({ updates }, signal);
    if (!res.ok) return null;
    return res.json();
  }

  static async sweepOrphanBundlesUntilClean(cartData, signal) {
    let data = cartData;
    for (let i = 0; i < 2; i += 1) {
      const next = await ThemeBundleCartRules.removeOrphanedBundleLinesFromCart(data, signal);
      if (!next) return data;
      data = next;
    }
    return data;
  }

  static async ensureCartHasNoOrphanBundleLines(signal) {
    const initial = await ThemeCartApi.fetchCart(signal);
    if (!ThemeBundleCartRules.cartHasOrphanBundleLines(initial)) return;
    await ThemeBundleCartRules.sweepOrphanBundlesUntilClean(initial, signal);
  }
}

class ThemeFreeGiftRules {
  static parseCurrencyLimitToCents(limitString, isoCode) {
    if (!limitString || !isoCode) return 0;
    const want = String(isoCode).trim().toUpperCase();
    const parts = String(limitString).replace(/;/g, ",").split(",");

    for (const part of parts) {
      const partTrimmed = part.trim();
      if (!partTrimmed || !partTrimmed.includes(":")) continue;

      const colonIndex = partTrimmed.indexOf(":");
      const currency = partTrimmed.slice(0, colonIndex).trim().toUpperCase();
      const rawAmount = partTrimmed.slice(colonIndex + 1).trim();
      const amountNum = Number(rawAmount);
      if (!Number.isFinite(amountNum)) continue;

      const amountCents = Math.round(amountNum * 100);
      if (amountCents <= 0) continue;

      if (currency === want) return amountCents;
    }

    return 0;
  }

  static propertyMatch(item, propertyKey, propertyValue) {
    const pk = String(propertyKey == null ? "" : propertyKey).toLowerCase();
    const keys = pk === "_free_gift" || pk === "free_gift"
      ? ["_free_gift", "free_gift"]
      : [String(propertyKey)];
    const want = String(propertyValue).trim().toLowerCase();
    for (let i = 0; i < keys.length; i++) {
      const raw = ThemeCartLineRules.propertyRaw(item, keys[i]);
      if (raw == null) continue;
      if (raw === true) return true;
      const s = String(raw).trim().toLowerCase();
      if (s === want || s === "true" || s === "yes" || s === "1" || s === "") return true;
    }
    return false;
  }

  static lineIds(item, variantIdStr, options = {}) {
    const giftPidOpt = options?.freeGiftProductId;
    const giftProductIdStr =
      giftPidOpt != null && giftPidOpt !== "" ? String(giftPidOpt) : "";
    const lineVariantId =
      item?.variant_id != null && item.variant_id !== ""
        ? String(item.variant_id)
        : String(item?.id ?? "");
    const lineProductId =
      item?.product_id != null && item.product_id !== "" ? String(item.product_id) : "";
    const matchesConfiguredVariant =
      Boolean(variantIdStr) && lineVariantId === String(variantIdStr);
    const matchesGiftProduct =
      Boolean(giftProductIdStr) && lineProductId === giftProductIdStr;
    return { matchesConfiguredVariant, matchesGiftProduct, lineVariantId, lineProductId };
  }

  static lineQualifiesAsThemeFreeGiftSlot(
    item,
    options,
    variantIdStr,
    propertyKey,
    propertyValue
  ) {
    if (ThemeFreeGiftRules.propertyMatch(item, propertyKey, propertyValue)) return true;
    if (ThemeCartLineRules.isBundleFrame(item)) return false;
    const { matchesConfiguredVariant, matchesGiftProduct } = ThemeFreeGiftRules.lineIds(
      item,
      variantIdStr,
      options
    );
    if (!matchesConfiguredVariant && !matchesGiftProduct) return false;
    return ThemeCartLineRules.lineTotalCents(item) === 0;
  }

  static lineExcludedFromPaidMerch(item, options, variantIdStr, propertyKey, propertyValue) {
    if (ThemeFreeGiftRules.propertyMatch(item, propertyKey, propertyValue)) return true;
    if (ThemeCartLineRules.isBundleFrame(item)) return false;
    const { matchesConfiguredVariant, matchesGiftProduct } = ThemeFreeGiftRules.lineIds(
      item,
      variantIdStr,
      options
    );
    if (!matchesConfiguredVariant && !matchesGiftProduct) return false;
    return ThemeCartLineRules.lineTotalCents(item) === 0;
  }
}

window.ThemeCart.CartApi = ThemeCartApi;
window.ThemeCart.CartLineRules = ThemeCartLineRules;
window.ThemeCart.BundleCartRules = ThemeBundleCartRules;
window.ThemeCart.FreeGiftRules = ThemeFreeGiftRules;
